import { useState, useEffect } from 'react';
import LandingNavigation from '@/components/LandingNavigation';
import LandingFooter from '@/components/LandingFooter';
import ContactFormDialog from '@/components/ContactFormDialog';
import TestimonialSlider from '@/components/TestimonialSlider';
import ProcessSteps from '@/components/ProcessSteps';
import PricingTabs from '@/components/PricingTabs';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { Shield, Award, Clock, FileCheck, Users, CheckCircle, TrendingUp, Target, Zap, Star } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { submitLead, countryCodes, getDefaultCountryCode } from '@/services/api';

export default function LandingPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact_number: '',
    country_code: '+1',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState({ name: 'Essentials', price: 45 });

  useEffect(() => {
    getDefaultCountryCode().then((code) => {
      setFormData((prev) => ({ ...prev, country_code: code }));
    });
  }, []);

  // Add scroll animations
  const [featuresRef, featuresVisible] = useScrollAnimation(0.1);
  const [trustRef, trustVisible] = useScrollAnimation(0.1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await submitLead(formData, {
      source: 'lp_banner',
      pageUrl: window.location.href,
      packageTitle: 'Essentials',
      packageAmount: 45,
      tmSignUpType: 1,
      onSuccess: (data) => {
        if (data.customerId) {
          window.location.href = `/thanks?customerid=${data.customerId}`;
        }
      },
    });

    if (result.success) {
      setFormData({
        name: '',
        email: '',
        contact_number: '',
        country_code: '+1',
        message: '',
      });
    }

    setLoading(false);
  };

  const features = [
    { icon: Shield, title: 'Complete Protection', description: 'Comprehensive trademark registration and monitoring' },
    { icon: Award, title: 'Expert Team', description: 'Experienced professionals handling your application' },
    { icon: Clock, title: 'Fast Processing', description: 'Quick turnaround with priority service options' },
    { icon: FileCheck, title: 'High Success Rate', description: '98% approval rate on trademark applications' },
    { icon: Users, title: 'Dedicated Support', description: 'Personal case manager for your trademark' },
    { icon: CheckCircle, title: 'Guaranteed Quality', description: 'Money-back guarantee on our services' },
  ];

  const faqs = [
    {
      question: 'Why do I need a trademark?',
      answer: 'A trademark protects your brand identity, prevents others from using similar marks, increases your business value, and establishes your legal ownership of your brand nationwide.',
    },
    {
      question: 'How long does registration take?',
      answer: 'The trademark registration process typically takes 8-12 months from filing to final registration, though timelines can vary based on the complexity of your application.',
    },
    {
      question: 'What happens if my application is rejected?',
      answer: 'We handle any office actions or rejections on your behalf. Our experienced team responds to USPTO inquiries and works to resolve any issues to get your trademark approved.',
    },
    {
      question: 'Can I trademark my business name and logo together?',
      answer: 'Yes! You can file separate applications for your business name and logo, or in some cases, file a combined mark. We will advise on the best strategy for your specific situation.',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="landing-page">
      <LandingNavigation />

      {/* Hero Section with Form */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden" data-testid="lp-hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1610631066894-62452ccb927c?crop=entropy&cs=srgb&fm=jpg&q=85)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#FFFAF0]/95 via-[#FFF5E6]/90 to-[#FFF8DC]/95"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div className="max-[767px]:order-last">
              {/* Badge */}
              <div className="inline-flex items-center space-x-2 bg-[#06214c]/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border border-[#06214c]/20">
                <Shield className="text-[#06214c]" size={18} />
                <span className="text-[#06214c] text-sm font-medium">Trusted by 10,000+ Businesses</span>
              </div>

              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#06214c] mb-6 leading-tight"
                style={{ fontFamily: 'Playfair Display' }}
                data-testid="lp-hero-title"
              >
                Protect Your Brand Legacy
              </h1>
              <p className="text-lg text-[#2C1810] mb-8" data-testid="lp-hero-subtitle">
                Secure your intellectual property with our comprehensive trademark registration services.
                Trusted by thousands of businesses across the USA.
              </p>

              {/* Trust Indicators */}
              <div className="flex flex-wrap gap-6 mb-8">
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 bg-[#06214c]/10 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <CheckCircle className="text-[#06214c]" size={20} />
                  </div>
                  <div>
                    <p className="text-[#2C1810] font-semibold">98% Success</p>
                    <p className="text-gray-600 text-sm">Approval Rate</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 bg-[#06214c]/10 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <Clock className="text-[#06214c]" size={20} />
                  </div>
                  <div>
                    <p className="text-[#2C1810] font-semibold">Fast Process</p>
                    <p className="text-gray-600 text-sm">Quick Turnaround</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 bg-[#06214c]/10 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <Users className="text-[#06214c]" size={20} />
                  </div>
                  <div>
                    <p className="text-[#2C1810] font-semibold">Expert Team</p>
                    <p className="text-gray-600 text-sm">24/7 Support</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Creative Form */}
            <div className="relative max-[767px]:order-first" data-testid="lp-banner-form">
              {/* Decorative background */}
              <div className="absolute -inset-4 bg-gradient-to-br from-[#D4A574]/20 to-[#06214c]/20 rounded-3xl blur-2xl"></div>

              <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/50">
                <div className="mb-6">
                  <h2 className="text-3xl font-bold text-[#06214c] mb-2" style={{ fontFamily: 'Playfair Display' }}>
                    Get Started Today
                  </h2>
                  <p className="text-gray-600">Fill out the form and our team will contact you shortly</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="lp-name" className="text-[#2C1810]">Name</Label>
                    <Input
                      id="lp-name"
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="border-gray-300 focus:border-[#06214c] focus:ring-[#06214c]"
                      data-testid="lp-form-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lp-email" className="text-[#2C1810]">Email</Label>
                    <Input
                      id="lp-email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="border-gray-300 focus:border-[#06214c] focus:ring-[#06214c]"
                      data-testid="lp-form-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lp-contact" className="text-[#2C1810]">Contact Number</Label>
                    <div className="flex space-x-2">
                      <Select
                        value={formData.country_code}
                        onValueChange={(value) => setFormData({ ...formData, country_code: value })}
                      >
                        <SelectTrigger className="w-[120px] border-gray-300" data-testid="lp-form-country-code">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {countryCodes.map((item) => (
                            <SelectItem key={item.code} value={item.code}>
                              {item.code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        id="lp-contact"
                        type="tel"
                        placeholder="123-456-7890"
                        value={formData.contact_number}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                          setFormData({ ...formData, contact_number: value });
                        }}
                        maxLength={10}
                        pattern="[0-9]*"
                        required
                        className="flex-1 border-gray-300 focus:border-[#06214c] focus:ring-[#06214c]"
                        data-testid="lp-form-phone"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="lp-message" className="text-[#2C1810]">Message</Label>
                    <Textarea
                      id="lp-message"
                      placeholder="Tell us about your trademark needs..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={3}
                      required
                      className="border-gray-300 focus:border-[#06214c] focus:ring-[#06214c] resize-none"
                      data-testid="lp-form-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full btn-primary"
                    disabled={loading}
                    data-testid="lp-form-submit"
                  >
                    {loading ? 'Submitting...' : 'Submit Request'}
                  </Button>
                </form>

                {/* Trust badges */}
                <div className="flex items-center justify-center gap-4 mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center space-x-1">
                    <Shield className="text-[#06214c]" size={16} />
                    <span className="text-xs text-gray-600">Secure</span>
                  </div>
                  <div className="w-px h-4 bg-gray-300"></div>
                  <div className="flex items-center space-x-1">
                    <CheckCircle className="text-[#06214c]" size={16} />
                    <span className="text-xs text-gray-600">Verified</span>
                  </div>
                  <div className="w-px h-4 bg-gray-300"></div>
                  <div className="flex items-center space-x-1">
                    <Award className="text-[#06214c]" size={16} />
                    <span className="text-xs text-gray-600">Trusted</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section with Image */}
      <section className="section bg-white" data-testid="lp-info-section">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Text Content */}
            <div className="flex flex-col h-full">
              <div className="inline-block bg-[#06214c]/10 px-6 py-3 rounded-full mb-6">
                <span className="text-[#06214c] font-semibold text-lg">Essential Brand Protection</span>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-[#2C1810] mb-6" style={{ fontFamily: 'Playfair Display' }}>
                Why Every Business Needs Trademark Protection
              </h2>
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                Without trademark protection, your brand name, logo, and business identity are vulnerable. Anyone could copy your brand, confuse customers, and damage your reputation—all without legal consequences.
              </p>
              <div className="space-y-4 mb-8 flex-grow">
                {[
                  {
                    icon: '⚠️',
                    title: 'Risk of Brand Theft',
                    text: 'Competitors can legally use similar names and logos without trademark protection'
                  },
                  {
                    icon: '💸',
                    title: 'Lost Revenue',
                    text: 'Brand confusion leads customers to competitors and costs you sales'
                  },
                  {
                    icon: '🛡️',
                    title: 'Legal Defense',
                    text: 'Trademark registration gives you exclusive rights and legal protection'
                  },
                  {
                    icon: '📈',
                    title: 'Business Growth',
                    text: 'Protected brands attract investors and command higher valuations'
                  }
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-4 p-5 bg-[#FFF5E6] rounded-2xl hover:shadow-lg transition-shadow duration-300">
                    <div className="text-4xl flex-shrink-0">{item.icon}</div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-[#06214c] mb-1" style={{ fontFamily: 'Playfair Display' }}>
                        {item.title}
                      </h4>
                      <p className="text-gray-600 text-lg">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setShowDialog(true)}
                className="btn-primary w-full text-center text-xl py-6 max-[360px]:text-sm max-[360px]:py-3"
              >
                Protect My Brand Now
              </button>
            </div>

            {/* Image */}
            <div className="lg:sticky lg:top-24">
              <div className="relative">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?crop=entropy&cs=srgb&fm=jpg&q=85" 
                    alt="Professional team collaboration"
                    className="w-full h-auto aspect-[4/5] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-[#06214c]/20 to-transparent"></div>
                </div>
                {/* Decorative elements */}
                <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-[#D4A574]/30 rounded-full blur-3xl"></div>
                <div className="absolute -top-6 -left-6 w-40 h-40 bg-[#06214c]/20 rounded-full blur-3xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Comparison Section */}
      <section className="section section-abstract-bg" data-testid="lp-benefits-section">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold text-[#06214c] mb-6" style={{ fontFamily: 'Playfair Display' }}>
              With vs. Without Trademark Protection
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See the difference trademark registration makes for your business
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Without Protection */}
            <div className="bg-red-50 rounded-3xl p-10 max-[576px]:p-5 border-2 border-red-200">
              <div className="text-center mb-8">
                <div className="inline-block bg-red-100 rounded-full p-4 mb-4">
                  <span className="text-5xl">❌</span>
                </div>
                <h3 className="text-3xl font-bold text-red-800 mb-3" style={{ fontFamily: 'Playfair Display' }}>
                  Without Trademark
                </h3>
              </div>
              <ul className="space-y-4">
                {[
                  'No legal protection for your brand name',
                  'Anyone can use similar names and logos',
                  'Difficult and expensive to fight copycats',
                  'Reduced brand value and credibility',
                  'No exclusive rights nationwide',
                  'Cannot use the ® symbol'
                ].map((item, index) => (
                  <li key={index} className="flex items-start space-x-3 text-gray-700 text-lg">
                    <span className="text-red-500 mt-1">✗</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* With Protection */}
            <div className="bg-green-50 rounded-3xl p-10 max-[576px]:p-5 border-2 border-green-300 relative">
              <div className="absolute -top-4 -right-4 bg-[#06214c] text-white px-6 py-2 rounded-full font-bold text-sm">
                RECOMMENDED
              </div>
              <div className="text-center mb-8">
                <div className="inline-block bg-green-100 rounded-full p-4 mb-4">
                  <span className="text-5xl">✅</span>
                </div>
                <h3 className="text-3xl font-bold text-green-800 mb-3" style={{ fontFamily: 'Playfair Display' }}>
                  With Trademark
                </h3>
              </div>
              <ul className="space-y-4">
                {[
                  'Exclusive legal rights to your brand',
                  'Stop competitors from using similar marks',
                  'Nationwide protection and enforcement',
                  'Increased business value (20-30%)',
                  'Federal court protection',
                  'Official ® trademark symbol'
                ].map((item, index) => (
                  <li key={index} className="flex items-start space-x-3 text-gray-700 text-lg">
                    <span className="text-green-600 mt-1">✓</span>
                    <span className="font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresRef} className="section bg-white" data-testid="lp-features-section">
        <div className="container mx-auto">
          <div className={`text-center mb-12 ${featuresVisible ? 'animate-fade-up' : 'opacity-0'}`}>
            <h2 className="text-5xl md:text-6xl font-bold text-[#06214c] mb-6" style={{ fontFamily: 'Playfair Display' }}>
              Why Choose Us for Trademark Registration?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We make trademark registration simple, affordable, and stress-free.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:grid mobile-slider">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index} 
                  className={`card text-center ${featuresVisible ? 'animate-scale' : 'opacity-0'} animate-stagger-${index + 1}`}
                  data-testid={`lp-feature-${index}`}
                >
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full flex items-center justify-center transform hover:rotate-12 transition-transform duration-300">
                      <Icon className="text-white" size={32} />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Steps - 3 Steps, No Scrolling */}
      <ProcessSteps />

      {/* Pricing Section */}
      <PricingTabs />

      {/* Mid-Page CTA Banner */}
      <section className="section relative overflow-hidden bg-gradient-to-r from-[#06214c] to-[#A0522D]" data-testid="lp-mid-cta-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#D4A574] rounded-full blur-3xl animate-pulse"></div>
        </div>
        
        <div className="container mx-auto text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="inline-block bg-white/20 px-6 py-2 rounded-full mb-6">
              <span className="text-white font-semibold text-lg">⚡ Limited Time Offer</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display' }}>
              Start Your Protection Today
            </h2>
            <p className="text-xl md:text-2xl text-white/95 mb-8 leading-relaxed">
              Join 10,000+ businesses who have secured their brands with us. File your trademark application in the next 24 hours and receive priority processing.
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-6 mb-10 max-[576px]:gap-3 max-[576px]:mb-6">
              <div className="flex items-center space-x-3 text-white bg-white/10 px-6 py-3 max-[576px]:px-3 max-[576px]:py-2 rounded-full backdrop-blur-sm">
                <CheckCircle size={28} className="flex-shrink-0 max-[576px]:w-5 max-[576px]:h-5" />
                <span className="text-lg font-semibold">Money-Back Guarantee</span>
              </div>
              <div className="flex items-center space-x-3 text-white bg-white/10 px-6 py-3 max-[576px]:px-3 max-[576px]:py-2 rounded-full backdrop-blur-sm">
                <CheckCircle size={28} className="flex-shrink-0 max-[576px]:w-5 max-[576px]:h-5" />
                <span className="text-lg font-semibold">98% Success Rate</span>
              </div>
              <div className="flex items-center space-x-3 text-white bg-white/10 px-6 py-3 max-[576px]:px-3 max-[576px]:py-2 rounded-full backdrop-blur-sm">
                <CheckCircle size={28} className="flex-shrink-0 max-[576px]:w-5 max-[576px]:h-5" />
                <span className="text-lg font-semibold">Expert Support</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
              <button
                onClick={() => setShowDialog(true)}
                className="bg-white text-[#06214c] px-10 py-3 rounded-full font-bold text-xl hover:bg-[#FFF5E6] transition-all transform hover:scale-105 shadow-2xl max-[360px]:text-sm max-[360px]:px-5 max-[360px]:py-2"
              >
                Get Started Now
              </button>
              <button
                onClick={() => setShowDialog(true)}
                className="bg-transparent border-3 border-white text-white px-10 py-3 rounded-full font-bold text-xl hover:bg-white hover:text-[#06214c] transition-all transform hover:scale-105 max-[360px]:text-sm max-[360px]:px-5 max-[360px]:py-2"
              >
                Free Consultation
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges Section - Creative */}
      <section ref={trustRef} className="section section-abstract-bg bg-[#FFF5E6]" data-testid="lp-trust-section">
        <div className="container mx-auto">
          <div className={`text-center mb-16 ${trustVisible ? 'animate-fade-up' : 'opacity-0'}`}>
            <h2 className="text-5xl md:text-6xl font-bold text-[#06214c] mb-6" style={{ fontFamily: 'Playfair Display' }}>
              Why Businesses Trust Us
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your success is our priority. Here's what sets us apart.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:grid mobile-slider">
            {[
              {
                icon: Shield,
                title: 'Money-Back Guarantee',
                description: 'If we can\'t file your trademark, you get a full refund. No questions asked.',
                gradient: 'from-[#06214c] to-[#D4A574]'
              },
              {
                icon: Zap,
                title: 'Lightning Fast Process',
                description: 'Get your application filed in as little as 24 hours. We work at your pace.',
                gradient: 'from-[#A0522D] to-[#06214c]'
              },
              {
                icon: Users,
                title: 'Dedicated Case Manager',
                description: 'Your personal trademark expert guides you through every step of the process.',
                gradient: 'from-[#06214c] to-[#A0522D]'
              },
              {
                icon: Star,
                title: 'No Hidden Fees',
                description: 'Transparent pricing with no surprises. What you see is what you pay.',
                gradient: 'from-[#D4A574] to-[#06214c]'
              },
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className={`group relative bg-white rounded-3xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 ${trustVisible ? 'animate-fade-up' : 'opacity-0'} animate-stagger-${index + 1}`}
                >
                  {/* Gradient background on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-0 group-hover:opacity-5 rounded-3xl transition-opacity duration-300`}></div>
                  
                  <div className="relative z-10">
                    <div className="flex items-start space-x-4">
                      <div className={`flex-shrink-0 w-16 h-16 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                        <Icon className="text-white" size={32} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>
                          {item.title}
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Slider */}
      <TestimonialSlider autoSlide={true} />

      {/* FAQ Section - Creative */}
      <section className="section bg-white" data-testid="lp-faq-section">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-[#06214c]/10 px-6 py-3 rounded-full mb-4">
              <span className="text-[#06214c] font-semibold text-lg">❓ Got Questions?</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-[#06214c] mb-6" style={{ fontFamily: 'Playfair Display' }}>
              Everything You Need to Know
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get answers to the most common questions about trademark registration
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-gradient-to-br from-[#FFF5E6] to-white rounded-3xl shadow-lg border-2 border-[#D4A574]/20 px-8 max-[576px]:px-4 overflow-hidden hover:shadow-2xl transition-shadow duration-300"
                >
                  <AccordionTrigger
                    className="text-2xl font-bold text-[#06214c] hover:text-[#A0522D] py-6 hover:no-underline"
                    style={{ fontFamily: 'Playfair Display' }}
                    data-testid={`lp-faq-question-${index}`}
                  >
                    <span className="flex items-start space-x-4">
                      <span className="text-3xl flex-shrink-0 text-[#D4A574]">Q{index + 1}.</span>
                      <span className="text-left">{faq.question}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent 
                    className="text-gray-600 text-lg leading-relaxed pb-6 pt-2 pl-16" 
                    data-testid={`lp-faq-answer-${index}`}
                  >
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* FAQ CTA */}
          <div className="text-center mt-12">
            <p className="text-xl text-gray-600 mb-6">Still have questions?</p>
            <button
              onClick={() => setShowDialog(true)}
              className="btn-primary text-xl"
            >
              Talk to Our Experts
            </button>
          </div>
        </div>
      </section>

      {/* Final CTA Banner */}
      <section className="section bg-gradient-to-r from-[#06214c] to-[#A0522D] text-white" data-testid="lp-cta-section">
        <div className="container mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>
            Ready to Protect Your Brand?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Don't wait until it's too late. Start your trademark registration today and safeguard your business's future.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <button
              onClick={() => setShowDialog(true)}
              className="bg-white text-[#06214c] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#FFF5E6] transition-all transform hover:scale-105"
              data-testid="lp-final-cta-register-btn"
            >
              Register My Trademark
            </button>
            <button
              
              className="js-chat bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-[#06214c] transition-all transform hover:scale-105"
              data-testid="lp-final-cta-chat-btn"
            >
              Chat Now
            </button>
          </div>
        </div>
      </section>

      <LandingFooter />
      <ContactFormDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        source="lp_page"
        packageTitle={selectedPlan.name}
        packageAmount={selectedPlan.price}
        tmSignUpType={1}
      />
    </div>
  );
}